import { Injectable, OnDestroy } from "@angular/core";
import { Apollo, gql } from "apollo-angular";
import { BehaviorSubject, Subscription } from "rxjs";
import { Notifications, NotificationType } from "./models/notification.model";
import { CurrentService } from "./current.service";
import { User } from "./models/user.model";
import { HttpClient } from "@angular/common/http";
import { environment } from "environments/environment";

// GraphQL Subscriptions
const NOTIFICATION_ADDED_SUBSCRIPTION = gql`
  subscription OnNotificationAdded($userId: String!) {
    onNotificationAdded(userId: $userId) {
      id
      userId
      title
      message
      type
      isRead
      createdAt
      updatedAt
      bookingId
      tripId
      paymentId
    }
  }
`;

const NOTIFICATION_READ_SUBSCRIPTION = gql`
  subscription OnNotificationRead($userId: String!) {
    onNotificationRead(userId: $userId) {
      id
      userId
      title
      message
      type
      isRead
      createdAt
      updatedAt
      bookingId
      tripId
      paymentId
    }
  }
`;

@Injectable({
  providedIn: "root",
})
export class NotificationGraphQLService implements OnDestroy {
  private subscriptions: Subscription[] = [];
  private user: User | null = null;
  private apiUrl = `${environment.apiUrl}/api/notifications`;

  // Sujets pour diffuser les notifications en temps réel
  private newNotificationSubject = new BehaviorSubject<Notifications | null>(null);
  private readNotificationSubject = new BehaviorSubject<Notifications | null>(null);
  private notificationCountSubject = new BehaviorSubject<number>(0);

  // Observables publics
  public newNotification$ = this.newNotificationSubject.asObservable();
  public readNotification$ = this.readNotificationSubject.asObservable();
  public notificationCount$ = this.notificationCountSubject.asObservable();

  constructor(
    private apollo: Apollo,
    private currentService: CurrentService,
    private http: HttpClient
  ) {
    // S'abonner aux changements d'utilisateur
    this.subscriptions.push(
      this.currentService.currentUser.subscribe((user) => {
        if (user && user.id !== this.user?.id) {
          this.user = user;
          this.startNotificationSubscriptions();
          this.loadInitialUnreadCount(); // Charger le compteur initial depuis l'API
        } else if (!user) {
          this.user = null;
          this.stopNotificationSubscriptions();
          this.notificationCountSubject.next(0);
        }
      })
    );
  }

  private loadInitialUnreadCount(): void {
    if (!this.user?.id) return;
    this.http
      .get<{ unreadCount: number }>(`${this.apiUrl}/user/${this.user.id}/unread-count`)
      .subscribe({
        next: (response) => {
          this.notificationCountSubject.next(response.unreadCount);
        },
        error: (error) => {
          console.error("❌ Erreur lors du chargement du compteur initial:", error);
        },
      });
  }

  private startNotificationSubscriptions(): void {
    if (!this.user?.id) {
      console.warn("⚠️ Tentative de démarrage des subscriptions sans utilisateur");
      return;
    }

    // Arrêter les précédentes subscriptions
    this.stopNotificationSubscriptions();

    console.log("🔔 Démarrage des subscriptions GraphQL pour:", this.user.id);

    // Subscription pour les nouvelles notifications
    const newNotificationSub = this.apollo
      .subscribe({
        query: NOTIFICATION_ADDED_SUBSCRIPTION,
        variables: { userId: this.user.id },
      })
      .subscribe({
        next: (result: any) => {
          console.log("📩 Événement GraphQL reçu (nouvelle notification):", result);

          if (result.data?.onNotificationAdded) {
            const notification = result.data.onNotificationAdded as Notifications;
            console.log(
              "✅ Notification ajoutée:",
              notification.title,
              "| Type:",
              notification.type
            );
            this.newNotificationSubject.next(notification);
            this.showNotificationToast(notification);

            // Recharger le compteur exact depuis l'API
            this.loadInitialUnreadCount();
          } else {
            console.warn("⚠️ Pas de données dans onNotificationAdded:", result);
          }
        },
        error: (error) => {
          console.error("❌ Erreur dans la subscription des nouvelles notifications:", error);
        },
      });

    // Subscription pour les notifications lues
    const readNotificationSub = this.apollo
      .subscribe({
        query: NOTIFICATION_READ_SUBSCRIPTION,
        variables: { userId: this.user.id },
      })
      .subscribe({
        next: (result: any) => {
          console.log("👁️ Événement GraphQL reçu (notification lue):", result);

          if (result.data?.onNotificationRead) {
            const notification = result.data.onNotificationRead as Notifications;
            console.log("✅ Notification marquée comme lue:", notification.title);
            this.readNotificationSubject.next(notification);

            // Recharger le compteur exact depuis l'API
            this.loadInitialUnreadCount();
          } else {
            console.warn("⚠️ Pas de données dans onNotificationRead:", result);
          }
        },
        error: (error) => {
          console.error("❌ Erreur dans la subscription des notifications lues:", error);
        },
      });

    this.subscriptions.push(newNotificationSub, readNotificationSub);
    console.log("✅ Subscriptions GraphQL démarrées avec succès");
  }

  private stopNotificationSubscriptions(): void {
    // Nettoyer uniquement les subscriptions GraphQL, pas l'abonnement à currentUser
    const graphqlSubs = this.subscriptions.slice(1); // Ignorer le premier (currentUser)
    graphqlSubs.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [this.subscriptions[0]]; // Garder seulement currentUser
  }

  private showNotificationToast(notification: Notifications): void {
    // Utiliser la bibliothèque de toast de votre choix (ex: ngx-toastr)
    const icon = this.getNotificationIcon(notification.type);

    // Exemple avec une notification native du navigateur
    // if ("Notification" in window && Notification.permission === "granted") {
    //   new Notification(notification.title, {
    //     body: notification.message,
    //     icon: `/assets/icons/${icon}.png`, // Chemin vers vos icônes
    //     badge: "/assets/icons/badge.png",
    //     tag: notification.id, // Pour éviter les doublons
    //   });
    // }
  }

  private getNotificationIcon(type: string): string {
    switch (type) {
      case NotificationType.APPOINTMENT:
        return "clock";
      case NotificationType.BOOKING:
        return "calendar";
      case NotificationType.PAYMENT:
        return "credit-card";
      case NotificationType.TRIP:
        return "map";
      case NotificationType.LOYALTY:
        return "award";
      case NotificationType.SYSTEM:
        return "bell";
      default:
        return "info";
    }
  }

  public setNotificationCount(count: number): void {
    this.notificationCountSubject.next(count);
  }

  public requestNotificationPermission(): void {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
        } else {
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
